// src/views/SetsView.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Play, CheckCircle2, Lock, Zap, X, Star, ChevronRight } from 'lucide-react';
import Mascot from '../components/Mascot';
import MasteryZone from '../components/hub/MasteryZone';

export default function SetsView({ unit, userProgress, startSet, setView, onBuyContent }) {
  // ✅ TODA LA LÓGICA ORIGINAL — SIN CAMBIOS
  const completedSets = userProgress.completedSets || [];
  const unlockedContent = userProgress.unlockedContent || [];
  const setIds = unit.sets.map(s => s.id);
  const completedCount = setIds.filter(id => completedSets.includes(id)).length;
  const totalSets = setIds.length;
  const isUnitFinished = completedCount === totalSets;
  const isExtraContent = unit.id?.includes('review') || unit.id?.includes('reading');
  const reviewId = `${unit.id}_review`;
  const readingId = `${unit.id}_reading`;
  const SPECIAL_COST = 250;
  const currentSkin = userProgress?.currentSkin || 'bribark';

  const [purchaseModal, setPurchaseModal] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [xpErrorModal, setXpErrorModal] = useState(null);
  const [showGuideMascot, setShowGuideMascot] = useState(false);

  const dollyXpMsgs = ["Ups… te faltan un poquito de XP 💕","Aún no alcanza, pero ya casi ✨","Un poco más de práctica y será tuyo 💖","Todavía no, pero vas muy bien 🌸","Sigamos jugando para ganar más XP 😊"];
  const bribarkXpMsgs = ["Eh… te faltan XP 😏","Casi, pero no… a seguir dandole! 🐾","Ese objeto pide más XP, campeón 💪","No alcanza… toca regresar a la \"chambing\" un poco 😈","Más XP primero, luego hablamos 😎"];
  const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

  useEffect(() => {
    const timerShow = setTimeout(() => setShowGuideMascot(true), 600);
    const timerHide = setTimeout(() => setShowGuideMascot(false), 5000);
    return () => { clearTimeout(timerShow); clearTimeout(timerHide); };
  }, []);

  const handleUnlockClick = (id, title, type, requiredRemaining) => {
    setPurchaseModal({ id, cost: SPECIAL_COST, title, type, requiredRemaining });
  };

  const confirmPurchase = () => {
    if (!purchaseModal) return;
    if (userProgress.xp >= purchaseModal.cost) {
      onBuyContent(purchaseModal.id, purchaseModal.cost);
      setPurchaseModal(null);
    } else {
      const missingXp = Math.max(purchaseModal.cost - (userProgress.xp || 0), 0);
      const msg = currentSkin.includes('dolly') ? pickRandom(dollyXpMsgs) : pickRandom(bribarkXpMsgs);
      setPurchaseModal(null);
      setXpErrorModal({ title: "¡Te faltan XP!", message: msg, missingXp });
    }
  };

  // ✅ HELPERS VISUALES (no afectan lógica)
  const nextSet = unit.sets.find(s => !completedSets.includes(s.id));
  const progressPct = totalSets > 0 ? Math.round((completedCount / totalSets) * 100) : 0;

  return (
    <div className="relative min-h-screen pb-32 overflow-x-hidden">

      {/* ── FONDO camino2 ── */}
<div className="fixed inset-0 z-0"
  style={{
    backgroundImage: "url('/images/camino2.png')",
    backgroundSize: '60% auto',      // ← fuerza exactamente el tamaño del viewport
    backgroundPosition: 'center 20%',
    backgroundRepeat: 'no-repeat',
  }}
/>

      {/* Overlay */}
      <div className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, rgba(10,20,60,0.80) 0%, rgba(10,20,60,0.55) 40%, rgba(10,20,60,0.80) 100%)'
        }}
      />

      {/* ── CONTENIDO PRINCIPAL ── */}
      <div className="relative z-10 space-y-4 animate-in slide-in-from-right duration-500">

        {/* ── HEADER GLASS ── */}
        <div className="sticky top-0 z-20 bg-white/10 backdrop-blur-md border-b border-white/15 px-4 pt-4 pb-3 shadow-xl">
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => setView('units')}
              className="p-2.5 rounded-xl bg-white/15 text-white hover:bg-white/25 transition-all border border-white/20 active:scale-95"
            >
              <ArrowLeft size={20} strokeWidth={2.5} />
            </button>

            <div className="flex-1">
              <p className="text-white/50 text-[9px] font-black uppercase tracking-widest">
                📍 Siguiente set: {nextSet?.title || 'Completo'}
              </p>
              <h2 className="text-white font-black text-base leading-tight">{unit.title}</h2>
            </div>

            {nextSet && (
              <button
                onClick={() => startSet(nextSet)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-black text-white text-xs border-b-2 border-green-800 active:border-b-0 active:translate-y-0.5 transition-all shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  boxShadow: '0 0 20px rgba(34,197,94,0.5), inset 0 1px 0 rgba(255,255,255,0.2)'
                }}
              >
                <Play size={12} fill="currentColor" /> Continuar
              </button>
            )}
          </div>

          {/* Barra XP */}
          <div className="space-y-1">
            <div className="flex justify-between text-[9px] font-black text-white/50 mb-1">
              <span>Sets completados: {completedCount}/{totalSets}</span>
              <span className="text-white/70">{completedCount * 50} XP</span>
            </div>
            <div className="h-2.5 bg-white/15 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${progressPct}%`,
                  background: 'linear-gradient(90deg, #38bdf8, #818cf8)',
                  boxShadow: '0 0 10px rgba(56,189,248,0.8)'
                }}
              />
            </div>
          </div>
        </div>

        {/* ── LISTA DE SETS ── */}
        <div className="px-4 max-w-lg mx-auto space-y-3">
          {unit.sets.map((set, index) => {
            const isCompleted = completedSets.includes(set.id);
            const isNext = set.id === nextSet?.id;

            return (
              <button
  key={set.id}
  onClick={() => startSet(set)}
  className={`w-full rounded-2xl flex items-center gap-3 p-4 transition-all duration-300 relative overflow-hidden border active:scale-[0.98]
    ${isCompleted
      ? 'bg-white/20 backdrop-blur-sm border-green-400/40'
      : isNext
      ? 'bg-white/25 backdrop-blur-md border-cyan-400/60'
      : 'bg-white/12 backdrop-blur-sm border-white/20'
    }`}
  style={isNext ? {
    boxShadow: '0 0 25px rgba(34,211,238,0.25), inset 0 1px 0 rgba(255,255,255,0.2), 0 4px 20px rgba(0,0,0,0.3)'
  } : isCompleted ? {
    boxShadow: '0 0 20px rgba(34,197,94,0.15), inset 0 1px 0 rgba(255,255,255,0.15), 0 4px 20px rgba(0,0,0,0.3)'
  } : {
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 4px 20px rgba(0,0,0,0.35)'
  }}
>

                {/* Línea brillo superior */}
                <div className="absolute top-0 left-4 right-4 h-px bg-white/25 pointer-events-none" />

                {/* Ícono medalla */}
                <div
                  className={`shrink-0 w-13 h-13 w-12 h-12 rounded-2xl flex items-center justify-center relative overflow-hidden
                    ${isCompleted
                      ? 'bg-gradient-to-br from-green-400 to-emerald-600'
                      : isNext
                      ? 'bg-gradient-to-br from-cyan-400 to-blue-600'
                      : 'bg-white/10'
                    }`}
                  style={isCompleted
                    ? { boxShadow: '0 4px 15px rgba(34,197,94,0.5)' }
                    : isNext
                    ? { boxShadow: '0 4px 15px rgba(34,211,238,0.5)' }
                    : {}}
                >
                  <div className="absolute top-0 left-0 right-0 h-1/2 bg-white/25 rounded-t-2xl pointer-events-none" />
                  {isCompleted
                    ? <CheckCircle2 size={22} className="text-white relative z-10" strokeWidth={2.5} />
                    : <span className="text-white font-black text-base relative z-10">{index + 1}</span>
                  }
                  {isNext && (
                    <div className="absolute inset-0 rounded-2xl border-2 border-cyan-300/60 animate-pulse pointer-events-none" />
                  )}
                </div>

                {/* Texto */}
                <div className="flex-1 text-left space-y-1">
                  <h3 className="font-black text-white text-sm leading-tight">{set.title}</h3>
                  <span className={`inline-block text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border
                    ${isCompleted
                      ? 'bg-green-400/20 text-green-300 border-green-400/30'
                      : isNext
                      ? 'bg-cyan-400/20 text-cyan-200 border-cyan-400/30'
                      : 'bg-white/8 text-white/40 border-white/10'
                    }`}
                  >
                    {isCompleted ? '✓ Perfecto' : isNext ? '▶ En curso' : 'Nuevo'}
                  </span>
                </div>

                {/* Estrellas + Play */}
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <div className="flex gap-0.5">
                    {[1, 2, 3].map(s => (
                      <Star key={s} size={9}
                        className={isCompleted ? 'text-amber-400 fill-amber-400' : 'text-white/20 fill-white/10'}
                      />
                    ))}
                  </div>
                  <div className={`p-2 rounded-xl transition-all
                    ${isCompleted ? 'bg-green-400/25 text-green-300' : isNext ? 'bg-cyan-400/25 text-cyan-200' : 'bg-white/10 text-white/30'}`}
                  >
                    <Play size={14} fill="currentColor" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── MASTERY ZONE ── */}
        {!isExtraContent && (
          <div className="px-4 max-w-lg mx-auto">
            <MasteryZone
              unit={unit}
              unlockedContent={unlockedContent}
              isUnitFinished={isUnitFinished}
              totalSets={totalSets}
              completedCount={completedCount}
              startSet={startSet}
              handleUnlockClick={handleUnlockClick}
              SPECIAL_COST={SPECIAL_COST}
            />
          </div>
        )}
      </div>

     {/* ── BADGE "Siguiente recomendado" ── */}
{nextSet && (
  <div className="fixed bottom-36 left-1/2 -translate-x-1/2 z-30 animate-in slide-in-from-bottom-4 duration-700">
    <button
      onClick={() => startSet(nextSet)}
      className="flex items-center gap-2 px-5 py-2.5 rounded-full font-black text-white text-xs shadow-2xl border border-orange-400/30 active:scale-95 transition-all"
      style={{
        background: 'linear-gradient(135deg, #f97316, #ea580c)',
        boxShadow: '0 4px 25px rgba(249,115,22,0.5)'
      }}
    >
      <span className="w-4 h-4 bg-white/25 rounded-full flex items-center justify-center text-[9px] font-black">1</span>
      Siguiente recomendado
      <ChevronRight size={13} strokeWidth={3} />
    </button>
  </div>
)}


      {/* ── MASCOTA GUÍA (fuera del contenedor animado) ── */}
      {showGuideMascot && (
        <div className="fixed top-20 right-2 z-50 max-w-[260px] animate-in slide-in-from-right-10 fade-in duration-700">
          <div className="relative">
            <button
              onClick={() => setShowGuideMascot(false)}
              className="absolute -top-2 -left-2 w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-lg hover:bg-slate-700 z-20"
            >✕</button>
            <div className="bg-white/15 backdrop-blur-md border border-white/25 p-3 rounded-2xl shadow-xl">
              <p className="text-[10px] font-bold text-white leading-relaxed pr-10">
                💡 ¡Pssst! Antes de empezar, revisa la <span className="text-cyan-300">Gramática</span> y el <span className="text-amber-300">Vocabulario</span> en el menú de abajo.
              </p>
            </div>
            <div className="absolute -bottom-8 -left-16 w-14 h-14 bg-white rounded-full border-4 border-cyan-400 shadow-lg flex items-center justify-center animate-bounce"
              style={{ boxShadow: '0 0 15px rgba(34,211,238,0.5)' }}>
              <Mascot type={currentSkin} />
            </div>
          </div>
        </div>
      )}

      {/* ── MODALES (fuera del div animado) ── */}
      {purchaseModal && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-[2.5rem] p-8 w-full max-w-sm text-center shadow-2xl border-4 border-indigo-100 relative animate-in zoom-in-95 duration-300">
            <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-32 h-32 drop-shadow-2xl">
              <Mascot type={currentSkin} />
            </div>
            <div className="mt-12 space-y-4">
              <h3 className="text-2xl font-black text-slate-800 leading-tight">
                ¿Desbloquear {purchaseModal.title}?
              </h3>
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 text-center">
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Requisito para abrir:</p>
                <p className="text-xs font-bold text-slate-600">
                  Completa <span className="text-indigo-600">{purchaseModal.requiredRemaining}</span> sets más para abrirlo gratis.
                </p>
              </div>
              <div className="bg-amber-50 p-5 rounded-3xl border-2 border-amber-100/50">
                <p className="text-xs font-bold text-amber-600 uppercase mb-1">Costo de Salto</p>
                <p className="text-3xl font-black text-amber-500 flex justify-center gap-2 items-center">
                  <Zap fill="currentColor" /> {purchaseModal.cost} XP
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <button onClick={() => setPurchaseModal(null)}
                  className="py-3 rounded-2xl font-black text-slate-500 bg-slate-100 hover:bg-slate-200 border-b-4 border-slate-200 active:border-b-0 active:translate-y-1 transition-all">
                  CANCELAR
                </button>
                <button onClick={confirmPurchase}
                  className="py-3 rounded-2xl font-black text-white bg-indigo-600 hover:bg-indigo-500 border-b-4 border-indigo-800 shadow-lg shadow-indigo-200 active:border-b-0 active:translate-y-1 transition-all">
                  CONFIRMAR
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {xpErrorModal && (
        <div className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-[2.5rem] p-8 w-full max-w-sm text-center shadow-2xl relative border-4 border-indigo-100 animate-in zoom-in-95 duration-200">
            <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-32 h-32 pointer-events-none">
              <Mascot type={currentSkin} />
            </div>
            <div className="mt-12 space-y-4">
              <h3 className="text-2xl font-black text-slate-700 leading-tight">{xpErrorModal.title}</h3>
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
                <p className="text-sm font-black text-slate-700 italic">"{xpErrorModal.message}"</p>
                <p className="text-xs font-bold text-slate-400 mt-2 uppercase tracking-widest">
                  Te faltan {xpErrorModal.missingXp} XP
                </p>
              </div>
              <button onClick={() => setXpErrorModal(null)}
                className="w-full py-3 rounded-2xl font-black text-white bg-slate-800 hover:bg-slate-700 border-b-4 border-slate-900 active:border-b-0 active:translate-y-1 transition-all">
                ENTENDIDO
              </button>
            </div>
          </div>
        </div>
      )}

      {errorMsg && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white p-6 rounded-[2rem] shadow-2xl max-w-xs w-full text-center animate-in zoom-in">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500">
              <X size={32} strokeWidth={3} />
            </div>
            <h3 className="text-lg font-black text-slate-800 mb-1">¡Ups!</h3>
            <p className="text-slate-500 font-medium text-sm mb-6">{errorMsg}</p>
            <button onClick={() => setErrorMsg(null)}
              className="w-full py-3 bg-slate-800 text-white rounded-xl font-bold hover:scale-105 transition-transform">
              ENTENDIDO
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
